import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import SendBirdChat, { ConnectionHandler, ConnectionState, SessionHandler } from '@sendbird/chat';
import { GroupChannelModule } from '@sendbird/chat/groupChannel';
import { Injectable } from '@angular/core';
import { Session } from '@services/session';
import { MessagingFeatureFlagService } from '@services/feature-flags/messaging';
import { MessagingWorkerProxyService } from '@app/dashboard/messaging/services/messaging-worker-proxy.service';
import { SendbirdTokenService } from './sendbird-token.service';
import { logger } from '@lib/datadog';
import { getDeviceType } from '@lib/utils';
import { isMobileView } from '@lib/screen-sizes';
import { genUniqId, getLoggedInUserId } from '@app/dashboard/messaging/lib/utils';
import { CONNECTION_STATE, LOGGING_TYPES } from '@app/dashboard/messaging/messaging.constants';
import { getSendBirdAppId } from '@lib/environments.const';
import * as i0 from "@angular/core";
import * as i1 from "@services/session";
import * as i2 from "@services/feature-flags/messaging";
import * as i3 from "@app/dashboard/messaging/services/messaging-worker-proxy.service";
import * as i4 from "./sendbird-token.service";
export var SENDBIRD_CONNECTION_STATE;
(function (SENDBIRD_CONNECTION_STATE) {
    SENDBIRD_CONNECTION_STATE["OPEN"] = "OPEN";
    SENDBIRD_CONNECTION_STATE["CLOSED"] = "CLOSED";
    SENDBIRD_CONNECTION_STATE["CONNECTING"] = "CONNECTING";
})(SENDBIRD_CONNECTION_STATE || (SENDBIRD_CONNECTION_STATE = {}));
export class SendbirdConnectionManagerV4Service {
    constructor(session, messagingFeatureFlagService, messagingWorkerProxyService, sendbirdTokenService) {
        this.messagingFeatureFlagService = messagingFeatureFlagService;
        this.messagingWorkerProxyService = messagingWorkerProxyService;
        this.sendbirdTokenService = sendbirdTokenService;
        this.onFocus$ = new Subject();
        this.onBlur$ = new Subject();
        this.onBeforeUnload$ = new Subject();
        this.sendbirdChatConnectionStateSubject = new BehaviorSubject({
            state: CONNECTION_STATE.CONNECTING,
        });
        this.RETRY_TIMEOUT = 2000;
        this.MAX_RETRY_ATTEMPTS = 3;
        const params = {
            appId: getSendBirdAppId(),
            localCacheEnabled: false,
            appStateToggleEnabled: false,
            modules: [
                new GroupChannelModule(),
            ],
        };
        this.sendBird = SendBirdChat.init(params);
        this.isSharedWorkerEnableForMessaging = this.messagingFeatureFlagService.isSharedWorkerEnableForMessaging();
        this.isMobileView = isMobileView();
        if (session.isChatAllowed() && !this.isMobileView) {
            if (this.isMessagingSharedWorkerEnabled) {
                this.connectWithSharedWorker();
            }
            else {
                this.connectWithSendBird();
            }
            fromEvent(window, 'focus').subscribe(() => this.onFocus$.next());
            fromEvent(window, 'blur').subscribe(() => this.onBlur$.next());
            fromEvent(window, 'beforeunload').subscribe(() => this.onBeforeUnload$.next());
        }
    }
    get sendbirdChatConnectionState$() {
        return this.sendbirdChatConnectionStateSubject.asObservable();
    }
    get isMessagingSharedWorkerEnabled() {
        return this.isSharedWorkerEnableForMessaging &&
            getDeviceType() === 'desktop' &&
            // @ts-ignore
            typeof (SharedWorker) !== 'undefined' &&
            !/^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
            (navigator.userAgent.indexOf('Opera') === -1 || navigator.userAgent.indexOf('OPR/') === -1);
    }
    disconnect() {
        if (this.sendBird)
            this.sendBird.disconnect();
        if (this.isMessagingSharedWorkerEnabled)
            this.messagingWorkerProxyService.logout();
    }
    connectWithSharedWorker() {
        this.messagingWorkerProxyService.connectWithSharedWorker();
        this.initSharedWorkerConnectionStream();
        this.getUptoDateConnectionStatusOnFocus();
        this.consumeOnPageUnloadEventForWorker();
    }
    connectWithSendBird(retryAttempt = 0) {
        if (retryAttempt > this.MAX_RETRY_ATTEMPTS) {
            logger.error(`Messaging: ${LOGGING_TYPES.CONNECTION}: connectWithSendBird: Failed to reconnect with Sendbird after ${this.MAX_RETRY_ATTEMPTS} retry attempts.`, {
                logDetail: {
                    logType: LOGGING_TYPES.CONNECTION,
                },
            });
            // Sendbird automatically tries to reconnect, this will update the internal connection state
            this.initConnectionHandler();
            return;
        }
        if (this.sendBird.connectionState !== 'OPEN') {
            this.sendbirdChatConnectionStateSubject.next({ state: CONNECTION_STATE.CONNECTING });
            if (this.messagingFeatureFlagService.isMessagingSessionAuthEnabled()) {
                this.initSessionHandler();
                this.sendbirdTokenService.fetchAuthToken()
                    .subscribe((authToken) => {
                    this.sendBird.connect(getLoggedInUserId().toString(), authToken)
                        .then((user) => {
                        if (user) {
                            this.sendbirdChatConnectionStateSubject.next({ state: CONNECTION_STATE.CONNECTED });
                            this.initConnectionHandler();
                            this.handleDisconnectOnBeforeUnloadEvent();
                        }
                    })
                        .catch((error) => {
                        if (error) {
                            if (this.messagingFeatureFlagService.isMessagingSendbirdConnectionRetryEnabled()) {
                                retryAttempt++;
                                setTimeout(() => this.connectWithSendBird(retryAttempt), this.RETRY_TIMEOUT * retryAttempt);
                            }
                        }
                    });
                });
            }
            else {
                this.sendBird.connect(getLoggedInUserId())
                    .then((user) => {
                    if (user) {
                        this.sendbirdChatConnectionStateSubject.next({ state: CONNECTION_STATE.CONNECTED });
                        this.initConnectionHandler();
                        this.handleDisconnectOnBeforeUnloadEvent();
                    }
                })
                    .catch((error) => {
                    if (error) {
                        if (this.messagingFeatureFlagService.isMessagingSendbirdConnectionRetryEnabled()) {
                            retryAttempt++;
                            setTimeout(() => this.connectWithSendBird(retryAttempt), this.RETRY_TIMEOUT * retryAttempt);
                        }
                    }
                });
            }
        }
    }
    initSharedWorkerConnectionStream() {
        this.messagingWorkerProxyService.sendbirdChatConnectionState$
            .subscribe(connectionState => {
            // emitting connection state to all the subscribers across the app.
            this.sendbirdChatConnectionStateSubject.next(connectionState);
            if (connectionState.state === CONNECTION_STATE.CONNECTED &&
                (!this.numberOfTabsInSW || this.numberOfTabsInSW !== connectionState.numberOfTabsInSW)) {
                this.numberOfTabsInSW = connectionState.numberOfTabsInSW;
            }
            if (connectionState.state === CONNECTION_STATE.INITIAL_CONNECT_FAILED) {
                logger.error(`Messaging:SharedWorker: ${LOGGING_TYPES.CONNECTION}: sharedWorker_connectWithSendBird: Failed to connect with Sendbird through shared worker.`, {
                    logDetail: {
                        logType: LOGGING_TYPES.CONNECTION,
                    },
                });
            }
            else if (connectionState.state === CONNECTION_STATE.RECONNECT_FAILED) {
                logger.error(`Messaging:SharedWorker: ${LOGGING_TYPES.CONNECTION}: sharedWorker_reconnect: Reconnect tries failed through shared worker.`, {
                    logDetail: {
                        logType: LOGGING_TYPES.CONNECTION,
                    },
                });
            }
        });
    }
    getUptoDateConnectionStatusOnFocus() {
        this.onFocus$
            .subscribe(() => {
            this.messagingWorkerProxyService.connectWithSharedWorker();
        });
    }
    consumeOnPageUnloadEventForWorker() {
        this.onBeforeUnload$
            .subscribe(() => {
            this.messagingWorkerProxyService.disconnect();
        });
    }
    initConnectionHandler() {
        this.sendBird.removeAllConnectionHandler();
        const sbConnectionHandler = new ConnectionHandler();
        sbConnectionHandler.onReconnectStarted = () => {
            console.info('SB reconnecting');
            this.sendbirdChatConnectionStateSubject.next({ state: CONNECTION_STATE.DISCONNECTED });
        };
        sbConnectionHandler.onReconnectSucceeded = () => {
            console.log('SB reconnected');
            this.sendbirdChatConnectionStateSubject.next({ state: CONNECTION_STATE.CONNECTED });
        };
        sbConnectionHandler.onReconnectFailed = () => {
            console.log('SB reconnectionFailed');
            logger.error(`Messaging: ${LOGGING_TYPES.ON_RECONNECT_FAILED_SENDBIRD_EVENT}: sbConnectionHandler.onReconnectFailed is called`, {
                logDetail: {
                    logType: LOGGING_TYPES.ON_RECONNECT_FAILED_SENDBIRD_EVENT,
                },
            });
        };
        if (this.sendBird.connectionState === ConnectionState.CLOSED) {
            this.sendBird.reconnect();
        }
        this.sendBird.addConnectionHandler(genUniqId(), sbConnectionHandler);
    }
    initSessionHandler() {
        const sessionHandler = new SessionHandler();
        sessionHandler.onSessionTokenRequired = (resolve, reject) => {
            this.sendbirdTokenService.fetchAuthToken()
                .subscribe((authToken) => {
                resolve(authToken);
            });
        };
        sessionHandler.onSessionClosed = () => {
            console.log('Shared Worker SB reconnectionFailed');
        };
        this.sendBird.setSessionHandler(sessionHandler);
    }
    handleDisconnectOnBeforeUnloadEvent() {
        this.onBeforeUnload$.subscribe(() => {
            this.disconnect();
        });
    }
}
SendbirdConnectionManagerV4Service.ɵfac = function SendbirdConnectionManagerV4Service_Factory(t) { return new (t || SendbirdConnectionManagerV4Service)(i0.ɵɵinject(i1.Session), i0.ɵɵinject(i2.MessagingFeatureFlagService), i0.ɵɵinject(i3.MessagingWorkerProxyService), i0.ɵɵinject(i4.SendbirdTokenService)); };
SendbirdConnectionManagerV4Service.ɵprov = i0.ɵɵdefineInjectable({ token: SendbirdConnectionManagerV4Service, factory: SendbirdConnectionManagerV4Service.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SendbirdConnectionManagerV4Service, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: i1.Session }, { type: i2.MessagingFeatureFlagService }, { type: i3.MessagingWorkerProxyService }, { type: i4.SendbirdTokenService }]; }, null); })();
